import { useEffect, useState } from "react"

import { Button, Col, Form, Modal, ModalBody } from "react-bootstrap"
import { FaSync } from "react-icons/fa"
import { useSelector } from "react-redux"
import { useHistory } from "react-router"

import DateTypeSelection from "./DateTypeSelection"
import AutoFilterCompaniesGet from "../../../api/Companies/AutoFilterCompaniesGet"
import AutoFilterCompany from "../../../api/Companies/CompanyModel/AutoFilterCompany"
import { CompanyDataLoader } from "../../../api/Reports/CompanyDataLoader"
import { DateType } from "../../../api/Reports/UnmergedRandomResultsReport/DateType"
import {
  UnmergedRandomResultsReportParams,
  UnmergedRandomResultsReportPdf,
} from "../../../api/Reports/UnmergedRandomResultsReportGet"
import { AppState } from "../../../AppState"
import { isPortalAdmin } from "../../../authentication/isAdminRole"
import { GetSelectedAssignedClinic } from "../../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { Home } from "../../../config/Routes"
import FormatDate from "../../../formatters/FormatDate"
import handleAPIError from "../../../utils/handleAPIError"
import printPdf from "../../../utils/printPdf"
import ActionButton from "../../ActionButton"
import DualListBox from "../../Administration/DualListBox"
import { AppPageWrapper } from "../../NavMenu/AppPageWrapper"
import FromToDates from "../FromToDates"

function UnmergedRandomResultsReport() {
  const portalAdmin = useSelector((state: AppState) => isPortalAdmin(state))
  const clinic = useSelector((state: AppState) => GetSelectedAssignedClinic(state))
  const history = useHistory()

  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [companies, setCompanies] = useState<CompanyDataLoader>({ data: [], loading: false })
  const [selectedCompanies, setSelectedCompanies] = useState<AutoFilterCompany[]>([])
  const [selectedDateType, setDateType] = useState<DateType>(DateType.COLLECTED)
  const [from, setFrom] = useState<Date>()
  const [to, setTo] = useState<Date>()

  useEffect(() => {
    setCompanies((prev) => ({ data: prev.data, loading: true }))
    const sub = AutoFilterCompaniesGet("").subscribe(
      (res) => setCompanies({ data: res, loading: false }),
      (error) => handleAPIError(error, () => setCompanies((prev) => ({ ...prev, loading: false })))
    )
    return () => sub.unsubscribe()
  }, [])

  useEffect(() => {
    setSelectedCompanies(companies.data)
  }, [companies.data])

  const isParamsValid = () => selectedCompanies.length > 0 && !!selectedDateType && !!from && !!to

  const getReportParams = (): UnmergedRandomResultsReportParams => {
    return {
      CompanyIds: selectedCompanies?.map((a) => a.AccountID) || [],
      DateType: selectedDateType,
      DateFrom: FormatDate(from),
      DateTo: FormatDate(to),
    }
  }

  const handleCancelClick = () => history.push(Home)

  const handleClearClick = () => {
    setSelectedCompanies(companies.data)
    setDateType(DateType.COLLECTED)
    setFrom(undefined)
    setTo(undefined)
  }

  const handleDateTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setDateType(event.target.value as DateType)

  const handleGeneratePdfClick = () => {
    setGeneratingPdf(true)
    UnmergedRandomResultsReportPdf(getReportParams()).subscribe(
      (results: string) => {
        setGeneratingPdf(false)
        printPdf(results)
      },
      (error) => handleAPIError(error, () => setGeneratingPdf(false))
    )
  }

  return (
    <AppPageWrapper parentClassName="align-items-center" showNav>
      <Modal.Dialog size="lg">
        <Modal.Header>
          <Modal.Title className="text-uppercase">Unmerged Random Results</Modal.Title>
        </Modal.Header>
        <ModalBody>
          <Form>
            {portalAdmin && (
              <Form.Row>
                <Col>
                  <Form.Label className="required">Companies</Form.Label>
                  <DualListBox
                    disabled={companies.loading}
                    options={companies.data}
                    selected={selectedCompanies}
                    idKey="AccountID"
                    labelKey="Name"
                    availableTitle="Available"
                    selectedTitle="Selected"
                    onChange={setSelectedCompanies}
                  />
                </Col>
              </Form.Row>
            )}
            {!portalAdmin && (
              <Form.Row className="form-group">
                <Form.Label column sm={2}>
                  Company
                </Form.Label>
                <Col>
                  <Form.Control as="input" readOnly value={clinic?.Name} />
                </Col>
              </Form.Row>
            )}
            <Form.Row className="form-group">
              <DateTypeSelection selectedType={selectedDateType} onChange={handleDateTypeChange} />
              <Col sm={1} />
            </Form.Row>
            <Form.Row className="form-group">
              <Col sm={2} />
              <Col>
                <FromToDates from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
              </Col>
              <Col sm={1} />
            </Form.Row>
          </Form>
        </ModalBody>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelClick}>
            Cancel
          </Button>
          <Button variant="secondary" className="mr-auto" onClick={handleClearClick}>
            Clear Filter&nbsp;
            <FaSync />
          </Button>
          <ActionButton
            variant="primary"
            disabled={companies.loading || !isParamsValid()}
            onClick={handleGeneratePdfClick}
            spin={generatingPdf}
            disableOnSpin
          >
            Continue
          </ActionButton>
        </Modal.Footer>
      </Modal.Dialog>
    </AppPageWrapper>
  )
}
export default UnmergedRandomResultsReport
